import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { Store, select } from '@ngrx/store'
import { ReplaySubject, merge } from 'rxjs'
import { filter } from 'rxjs/operators'
import { loadSuffixes, selectAllSuffixes } from 'src/app/store/suffixes'
import {
  FileInfo,
  OfferAttachmentsComponent,
} from 'src/pages/admin/auction/supplier-offer-overlay/offer-attachments/offer-attachments.component'

export interface ProductAttachmentsDialogOptions {
  productName: string
  // saved state (from company.products_spec row)
  fileIds: string[]
  productPicFileId?: string
  techSpecFileId?: string
  // staged state from a previous dialog session (takes precedence over saved state)
  stagedFiles?: FileInfo[]
}

export interface ProductAttachmentsDialogResult {
  files: FileInfo[]
}

/**
 * Attachments of a supplier's product (WA-22050).
 *
 * Only stages changes in memory — uploads, archive/restore (WA-22618) and the product pic pointer
 * and tech spec pointers (WA-22611) are persisted by the company profile form's Save.
 */
@Component({
  selector: 'tc-product-attachments-dialog',
  templateUrl: './product-attachments-dialog.component.html',
  styleUrl: './product-attachments-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    OfferAttachmentsComponent,
  ],
})
export class ProductAttachmentsDialogComponent implements OnInit {
  private readonly store = inject(Store)
  private readonly dialogRef = inject<MatDialogRef<ProductAttachmentsDialogComponent, ProductAttachmentsDialogResult>>(MatDialogRef)
  private readonly dialogData = inject<ProductAttachmentsDialogOptions>(MAT_DIALOG_DATA)


  protected readonly productName = this.dialogData.productName
  protected readonly productPicFileId = this.dialogData.productPicFileId
  protected readonly techSpecFileId = this.dialogData.techSpecFileId
  protected readonly fileIds$ = new ReplaySubject<string[]>(1)
  protected readonly restoreFileInfos$ = new ReplaySubject<FileInfo[]>(1)
  protected readonly suffixes$ = this.store.pipe(select(selectAllSuffixes))

  // `null` until the attachments component reports its state — Apply is a no-op before that
  private currentFiles: FileInfo[] = null

  ngOnInit() {
    this.dialogRef.disableClose = true
    merge(
      this.dialogRef.backdropClick(),
      this.dialogRef.keydownEvents().pipe(filter(event => event.key === 'Escape')),
    ).subscribe(() => this.close())

    this.store.dispatch(loadSuffixes())
    if (this.dialogData.stagedFiles) {
      // reopened with staged (not yet saved) changes — clone so Cancel doesn't mutate them
      this.currentFiles = this.dialogData.stagedFiles.map(f => ({ ...f }))
      this.fileIds$.next([])
      this.restoreFileInfos$.next(this.currentFiles)
    } else {
      this.fileIds$.next(this.dialogData.fileIds || [])
    }
  }

  protected onFilesChange(files: FileInfo[]) {
    this.currentFiles = files
  }

  protected close() {
    // null until the attachments component reports its state — close without changes then
    this.dialogRef.close(this.currentFiles ? { files: this.currentFiles } : undefined)
  }
}
